import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Check,
  Download,
  FilePlus2,
  FileText,
  Link2,
  LoaderCircle,
  MessageCircle,
  Save,
  Search,
  Trash2,
  X,
} from "lucide-react";
import { encodeCuijPath } from "@tribunalea/shared";
import {
  DocumentEditor,
  emptyDocument,
  plainTextToDocument,
} from "@tribunalea/document-editor";
import { ConfirmDialog, useRouter } from "@tribunalea/ui";

function updatedLabel(value) {
  if (!value) return "Sin guardar";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Sin guardar";
  return new Intl.DateTimeFormat("es-AR", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function normalizeSearchValue(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("es")
    .trim();
}

function matchesSearch(values, query) {
  const normalizedQuery = normalizeSearchValue(query);
  if (!normalizedQuery) return true;
  const searchable = normalizeSearchValue(values.filter(Boolean).join(" "));
  if (searchable.includes(normalizedQuery)) return true;
  const compactQuery = normalizedQuery.replace(/[^a-z0-9]/g, "");
  return compactQuery.length > 0
    && searchable.replace(/[^a-z0-9]/g, "").includes(compactQuery);
}

function documentExpediente(document, expedientes) {
  const context = document.context || {};
  const snapshot = context.snapshot || {};
  const current = expedientes.find(({ cuij }) => cuij === context.id) || {};
  return {
    cuij: context.id || snapshot.cuij || current.cuij || "",
    numero: context.numero || snapshot.numero || current.numero || "",
    caratula:
      snapshot.caratula
      || context.label
      || current.caratula
      || "Sin carátula informada",
  };
}

function writingContextIsReusable(writingContext) {
  const reusableUntil = new Date(
    writingContext?.reusableUntil || 0,
  ).getTime();
  return writingContext?.status === "ready"
    && Number.isFinite(reusableUntil)
    && reusableUntil > Date.now();
}

function signatureIsConfigured(signature = {}) {
  const value = signature || {};
  return Boolean(
    value.imageDataUrl
    || value.displayName
    || value.role
    || value.registration,
  );
}

function NewWorkspaceDocumentDialog({
  busy,
  error,
  expedientes,
  fixedExpediente = null,
  onClose,
  onCreate,
}) {
  const [query, setQuery] = useState("");
  const [documentTitle, setDocumentTitle] = useState("");
  const [selectedCuij, setSelectedCuij] = useState(
    fixedExpediente?.cuij || "",
  );
  const filteredExpedientes = useMemo(
    () => expedientes.filter((expediente) => matchesSearch([
      expediente.cuij,
      expediente.numero,
      expediente.caratula,
    ], query)),
    [expedientes, query],
  );

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape" && !busy) onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [busy, onClose]);

  return (
    <div
      className="modal-backdrop workspace-document-picker-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !busy) onClose();
      }}
      role="presentation"
    >
      <form
        aria-labelledby="workspace-document-picker-title"
        aria-modal="true"
        className="dialog-card workspace-document-picker"
        onSubmit={(event) => {
          event.preventDefault();
          const title = documentTitle.trim();
          if (selectedCuij && title && !busy) {
            onCreate({ cuij: selectedCuij, title });
          }
        }}
        role="dialog"
      >
        <button
          aria-label="Cerrar"
          className="icon-button dialog-close"
          disabled={busy}
          onClick={onClose}
          type="button"
        >
          <X size={18} />
        </button>
        <div className="dialog-icon primary"><FilePlus2 size={20} /></div>
        <h2 id="workspace-document-picker-title">Crear un escrito</h2>
        <p>
          {fixedExpediente
            ? "Asignale un nombre. Quedará vinculado a este expediente."
            : "Asignale un nombre y elegí el expediente al que quedará vinculado."}
        </p>
        <label className="workspace-document-picker-title-field">
          <span>Nombre del escrito</span>
          <input
            autoFocus
            maxLength={240}
            onChange={(event) => setDocumentTitle(event.target.value)}
            placeholder="Ej.: Recurso de reposición"
            value={documentTitle}
          />
        </label>
        {fixedExpediente ? (
          <div className="workspace-document-picker-fixed-context">
            <Link2 size={16} />
            <span>
              <strong>
                {fixedExpediente.cuij}
                {fixedExpediente.numero
                  ? ` · N.º ${fixedExpediente.numero}`
                  : ""}
              </strong>
              <small>
                {fixedExpediente.caratula || "Sin carátula informada"}
              </small>
            </span>
          </div>
        ) : (
          <>
            <label className="workspace-document-picker-search">
              <Search size={16} />
              <input
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Buscar por carátula, CUIJ o N.º de expediente"
                type="search"
                value={query}
              />
            </label>
            <div className="workspace-document-picker-list">
              {filteredExpedientes.length ? filteredExpedientes.map((expediente) => {
                const number = String(expediente.numero || "").trim();
                return (
                  <button
                    aria-pressed={selectedCuij === expediente.cuij}
                    className={selectedCuij === expediente.cuij ? "is-selected" : ""}
                    key={expediente.cuij}
                    onClick={() => setSelectedCuij(expediente.cuij)}
                    type="button"
                  >
                    <span>
                      <strong>{expediente.caratula || "Sin carátula informada"}</strong>
                      <small>
                        {expediente.cuij}{number ? ` · N.º ${number}` : ""}
                      </small>
                    </span>
                    {expediente.archived && <em>Archivado</em>}
                    <Check size={16} />
                  </button>
                );
              }) : (
                <div className="workspace-document-picker-empty">
                  No encontramos expedientes con esa búsqueda.
                </div>
              )}
            </div>
          </>
        )}
        {error && <div className="inline-error">{error}</div>}
        <div className="dialog-actions">
          <button
            className="button secondary"
            disabled={busy}
            onClick={onClose}
            type="button"
          >
            Cancelar
          </button>
          <button
            className="button primary"
            disabled={!selectedCuij || !documentTitle.trim() || busy}
            type="submit"
          >
            {busy ? <LoaderCircle className="spin" size={16} /> : <FilePlus2 size={16} />}
            {busy ? "Creando…" : "Crear escrito"}
          </button>
        </div>
      </form>
    </div>
  );
}

function download({ blob, fileName }) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 500);
}

export function WorkspaceDrafts({
  api,
  basePath = "/escritos",
  contextCuij = "",
  expedientes,
  expedientesLoading = false,
  indexOnly = false,
  selectedDocumentId,
}) {
  const { navigate } = useRouter();
  const [documents, setDocuments] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [conversationCursor, setConversationCursor] = useState(null);
  const [conversationsLoading, setConversationsLoading] = useState(true);
  const [conversationsLoadingMore, setConversationsLoadingMore] = useState(false);
  const [conversationError, setConversationError] = useState("");
  const [active, setActive] = useState(null);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState(() => emptyDocument());
  const [includeSignature, setIncludeSignature] = useState(false);
  const [legalSignature, setLegalSignature] = useState(null);
  const [signatureLoading, setSignatureLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [documentLoading, setDocumentLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [creationDialogOpen, setCreationDialogOpen] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [writingContext, setWritingContext] = useState({
    status: "not_requested",
    error: null,
  });
  const [documentQuery, setDocumentQuery] = useState("");
  const [error, setError] = useState("");
  const latestDraft = useRef({
    title: "",
    content: emptyDocument(),
    includeSignature: false,
  });
  const contextPreparationSequence = useRef(0);
  const fixedExpediente = useMemo(
    () => contextCuij
      ? expedientes.find(({ cuij }) => cuij === contextCuij) || {
        cuij: contextCuij,
        caratula: "Expediente vinculado",
      }
      : null,
    [contextCuij, expedientes],
  );

  const loadList = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const items = await api.listWorkspaceDocuments({
        productId: "praxis",
        contextType: "expediente",
        ...(contextCuij ? { contextId: contextCuij } : {}),
      });
      setDocuments(items);
    } catch (loadError) {
      setError(loadError.message);
    } finally {
      setLoading(false);
    }
  }, [api, contextCuij]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const loadConversations = useCallback(async ({
    append = false,
    cursor = null,
  } = {}) => {
    if (append) setConversationsLoadingMore(true);
    else setConversationsLoading(true);
    setConversationError("");
    try {
      const result = await api.listJustinianoConversations({
        limit: 50,
        cursor,
        assignedOnly: !contextCuij,
        ...(contextCuij
          ? { contextType: "expediente", contextId: contextCuij }
          : {}),
      });
      setConversations((current) => append
        ? [...current, ...(result.items || [])]
        : (result.items || []));
      setConversationCursor(result.nextCursor || null);
    } catch (loadError) {
      setConversationError(loadError.message);
    } finally {
      setConversationsLoading(false);
      setConversationsLoadingMore(false);
    }
  }, [api, contextCuij]);

  useEffect(() => {
    void loadConversations();
  }, [loadConversations]);

  useEffect(() => {
    const refreshAssignments = (event) => {
      if (event.detail?.appId && event.detail.appId !== "praxis") return;
      void loadConversations();
    };
    window.addEventListener(
      "justiniano:conversation-assignment-changed",
      refreshAssignments,
    );
    return () => window.removeEventListener(
      "justiniano:conversation-assignment-changed",
      refreshAssignments,
    );
  }, [loadConversations]);

  useEffect(() => {
    let cancelled = false;
    setSignatureLoading(true);
    api.profile()
      .then((profile) => {
        if (!cancelled) setLegalSignature(profile?.legalSignature || null);
      })
      .catch(() => {
        if (!cancelled) setLegalSignature(null);
      })
      .finally(() => {
        if (!cancelled) setSignatureLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [api]);

  useEffect(() => {
    if (!selectedDocumentId) {
      setActive(null);
      setTitle("");
      setContent(emptyDocument());
      setIncludeSignature(false);
      setWritingContext({ status: "not_requested", error: null });
      setDirty(false);
      return;
    }
    let cancelled = false;
    setDocumentLoading(true);
    setError("");
    api.getWorkspaceDocument(selectedDocumentId)
      .then((document) => {
        if (cancelled) return;
        if (contextCuij && document.context?.id !== contextCuij) {
          setError("Este escrito pertenece a otro expediente.");
          navigate(basePath, { replace: true });
          return;
        }
        setActive(document);
        setTitle(document.title);
        setContent(document.content || emptyDocument());
        setIncludeSignature(Boolean(document.includeSignature));
        setWritingContext(document.writingContext || {
          status: "not_requested",
          error: null,
        });
        latestDraft.current = {
          title: document.title,
          content: document.content || emptyDocument(),
          includeSignature: Boolean(document.includeSignature),
        };
        setDirty(false);
      })
      .catch((loadError) => {
        if (!cancelled) setError(loadError.message);
      })
      .finally(() => {
        if (!cancelled) setDocumentLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [api, basePath, contextCuij, navigate, selectedDocumentId]);

  const prepareWritingContext = useCallback(async (
    documentId,
    { force = false } = {},
  ) => {
    if (!documentId) return;
    const sequence = contextPreparationSequence.current + 1;
    contextPreparationSequence.current = sequence;
    setWritingContext((current) => ({
      ...current,
      status: "processing",
      error: null,
    }));
    try {
      const prepared = await api.prepareWorkspaceDocumentContext(
        documentId,
        { force },
      );
      if (contextPreparationSequence.current === sequence) {
        setWritingContext(prepared);
      }
    } catch (preparationError) {
      if (contextPreparationSequence.current === sequence) {
        setWritingContext({
          status: "failed",
          error:
            preparationError.message
            || "No pudimos preparar el contexto del expediente.",
        });
      }
    }
  }, [api]);

  useEffect(() => {
    if (!active?.id || !active.context?.id) return undefined;
    if (writingContextIsReusable(active.writingContext)) return undefined;
    if (active.writingContext?.status === "failed") return undefined;
    prepareWritingContext(active.id);
    return () => {
      contextPreparationSequence.current += 1;
    };
  }, [
    active?.context?.id,
    active?.id,
    active?.writingContext?.reusableUntil,
    active?.writingContext?.status,
    prepareWritingContext,
  ]);

  useEffect(() => {
    latestDraft.current = { title, content, includeSignature };
  }, [content, includeSignature, title]);

  const mergeDocument = useCallback((document) => {
    setActive(document);
    setDocuments((current) => {
      const next = current.filter(({ id }) => id !== document.id);
      return [document, ...next];
    });
  }, []);

  const saveNow = useCallback(async () => {
    if (!active || saving || !dirty || !title.trim()) return active;
    const submitted = latestDraft.current;
    setSaving(true);
    setError("");
    try {
      const saved = await api.updateWorkspaceDocument(active.id, {
        title: submitted.title,
        content: submitted.content,
        includeSignature: submitted.includeSignature,
        expectedRevision: active.revision,
      });
      mergeDocument(saved);
      const latest = latestDraft.current;
      if (
        latest.title === submitted.title
        && JSON.stringify(latest.content) === JSON.stringify(submitted.content)
        && latest.includeSignature === submitted.includeSignature
      ) {
        setDirty(false);
      }
      return saved;
    } catch (saveError) {
      setError(saveError.message);
      throw saveError;
    } finally {
      setSaving(false);
    }
  }, [active, api, dirty, mergeDocument, saving, title]);

  useEffect(() => {
    if (!dirty || !active || saving || !title.trim()) return undefined;
    const timeout = window.setTimeout(() => {
      saveNow().catch(() => {});
    }, 900);
    return () => window.clearTimeout(timeout);
  }, [active, dirty, saveNow, saving, title]);

  const openDocument = async (document) => {
    if (document.id === active?.id) return;
    try {
      if (dirty) await saveNow();
      if (indexOnly) {
        const expediente = documentExpediente(document, expedientes);
        if (!expediente.cuij) {
          setError("El escrito no tiene un expediente vinculado.");
          return;
        }
        navigate(
          `/expedientes/${encodeCuijPath(expediente.cuij)}/escritos/${document.id}`,
        );
        return;
      }
      navigate(`${basePath}/${document.id}`);
    } catch {
      // El mensaje de guardado queda visible y evita perder cambios al navegar.
    }
  };

  const createBlank = async ({ cuij: requestedCuij, title: requestedTitle }) => {
    const cuij = contextCuij || requestedCuij;
    const requestedTitleTrimmed = String(requestedTitle || "").trim();
    if (!cuij) {
      setError("Elegí un expediente antes de crear el escrito.");
      return;
    }
    if (!requestedTitleTrimmed) {
      setError("Ingresá un nombre para el escrito.");
      return;
    }
    setCreating(true);
    setError("");
    try {
      if (dirty) await saveNow();
      const created = await api.createWorkspaceDocument({
        productId: "praxis",
        documentType: "legal_filing",
        title: requestedTitleTrimmed,
        content: plainTextToDocument("SEÑOR JUEZ:"),
        context: { type: "expediente", id: cuij },
        origin: { type: "manual" },
      });
      setDocuments((current) => [created, ...current]);
      setCreationDialogOpen(false);
      navigate(`${basePath}/${created.id}`);
    } catch (createError) {
      setError(createError.message);
    } finally {
      setCreating(false);
    }
  };

  const markReady = async () => {
    if (!active) return;
    try {
      if (dirty) await saveNow();
      const current = dirty
        ? await api.getWorkspaceDocument(active.id)
        : active;
      const updated = await api.updateWorkspaceDocument(active.id, {
        status: current.status === "ready" ? "draft" : "ready",
        expectedRevision: current.revision,
      });
      mergeDocument(updated);
    } catch (statusError) {
      setError(statusError.message);
    }
  };

  const exportWord = async () => {
    if (!active) return;
    setExporting(true);
    setError("");
    try {
      if (dirty) await saveNow();
      download(await api.exportWorkspaceDocument(active.id));
    } catch (exportError) {
      setError(exportError.message);
    } finally {
      setExporting(false);
    }
  };

  const deleteDocument = async () => {
    if (!active || deleting) return;
    setDeleting(true);
    setError("");
    try {
      await api.deleteWorkspaceDocument(active.id);
      const remaining = documents.filter(({ id }) => id !== active.id);
      setDocuments(remaining);
      setDeleteConfirmationOpen(false);
      setActive(null);
      setWritingContext({ status: "not_requested", error: null });
      navigate(basePath, { replace: true });
    } catch (deleteError) {
      setError(deleteError.message);
    } finally {
      setDeleting(false);
    }
  };

  const closeDocument = async () => {
    try {
      if (dirty) await saveNow();
      navigate(basePath);
    } catch {
      // El error de guardado permanece visible y el editor sigue abierto.
    }
  };

  const filteredDocuments = useMemo(
    () => documents.filter((document) => {
      const expediente = documentExpediente(document, expedientes);
      return matchesSearch([
        document.title,
        expediente.cuij,
        expediente.numero,
      ], documentQuery);
    }),
    [documentQuery, documents, expedientes],
  );
  const filteredConversations = useMemo(
    () => conversations.filter((conversation) => matchesSearch([
      conversation.title,
      conversation.preview,
      conversation.assignment?.contextId,
      conversation.assignment?.contextLabel,
    ], documentQuery)),
    [conversations, documentQuery],
  );

  const openConversation = (conversation) => {
    window.dispatchEvent(new CustomEvent("justiniano:open-conversation", {
      detail: { appId: "praxis", conversationId: conversation.id },
    }));
  };

  const statusLabel = useMemo(() => {
    if (saving) return "Guardando…";
    if (dirty) return "Cambios pendientes";
    if (active) return `Guardado · ${updatedLabel(active.updatedAt)}`;
    return "";
  }, [active, dirty, saving]);
  const signatureConfigured = signatureIsConfigured(legalSignature);

  return (
    <section
      className={`expediente-drafts-workspace workspace-drafts-workspace ${
        indexOnly || !selectedDocumentId ? "is-list-only" : "is-editor-only"
      }`}
    >
      {(indexOnly || !selectedDocumentId) && (
      <aside className="expediente-drafts-sidebar">
        <header>
          <div>
            <small>{indexOnly ? "TODOS LOS EXPEDIENTES" : "ESTE EXPEDIENTE"}</small>
            <h2>Escritos y conversaciones</h2>
          </div>
          {!indexOnly && (
            <button
              aria-label="Crear escrito"
              className="button primary"
              disabled={creating || expedientesLoading || !fixedExpediente}
              onClick={() => {
                setError("");
                setCreationDialogOpen(true);
              }}
              title="Crear escrito"
              type="button"
            >
              {creating
                ? <LoaderCircle className="spin" size={17} />
                : <FilePlus2 size={17} />}
              Nuevo escrito
            </button>
          )}
        </header>
        {(documents.length > 0 || conversations.length > 0) && (
          <label className="workspace-drafts-search">
            <Search size={15} />
            <input
              aria-label="Buscar escritos y conversaciones"
              onChange={(event) => setDocumentQuery(event.target.value)}
              placeholder="Buscar escritos y conversaciones"
              type="search"
              value={documentQuery}
            />
          </label>
        )}
        {conversationError && (
          <div className="inline-error expediente-draft-error">
            {conversationError}
          </div>
        )}
        {loading || conversationsLoading ? (
          <div className="expediente-drafts-sidebar-state">
            <LoaderCircle className="spin" size={18} />
            Cargando escritos y conversaciones…
          </div>
        ) : filteredDocuments.length || filteredConversations.length ? (
          <div className="workspace-artifact-sections">
            {filteredDocuments.length > 0 && (
              <section>
                <h3>Escritos</h3>
                <div className="expediente-drafts-list">
                  {filteredDocuments.map((document) => {
                    const expediente = documentExpediente(document, expedientes);
                    const number = String(expediente.numero || "").trim();
                    return (
                      <button
                        className={document.id === active?.id ? "is-active" : ""}
                        key={document.id}
                        onClick={() => openDocument(document)}
                        type="button"
                      >
                        <FileText size={16} />
                        <span>
                          <strong>{document.title}</strong>
                          <small>
                            {expediente.cuij || "Sin expediente"}
                            {number ? ` · N.º ${number}` : ""}
                          </small>
                          <small>{updatedLabel(document.updatedAt)}</small>
                        </span>
                        {document.status === "ready" && <Check size={14} />}
                      </button>
                    );
                  })}
                </div>
              </section>
            )}
            {filteredConversations.length > 0 && (
              <section>
                <h3>Conversaciones</h3>
                <div className="expediente-drafts-list is-conversations">
                  {filteredConversations.map((conversation) => (
                    <button
                      key={conversation.id}
                      onClick={() => openConversation(conversation)}
                      type="button"
                    >
                      <MessageCircle size={16} />
                      <span>
                        <strong>{conversation.title || "Nueva conversación"}</strong>
                        <small>
                          {conversation.assignment?.contextId || "Expediente vinculado"}
                        </small>
                        <small>{updatedLabel(conversation.updatedAt)}</small>
                      </span>
                    </button>
                  ))}
                </div>
                {conversationCursor && (
                  <button
                    className="workspace-conversations-more"
                    disabled={conversationsLoadingMore}
                    onClick={() => void loadConversations({
                      append: true,
                      cursor: conversationCursor,
                    })}
                    type="button"
                  >
                    {conversationsLoadingMore && (
                      <LoaderCircle className="spin" size={14} />
                    )}
                    Ver más conversaciones
                  </button>
                )}
              </section>
            )}
          </div>
        ) : documents.length || conversations.length ? (
          <div className="expediente-drafts-sidebar-state is-empty">
            <Search size={22} />
            <strong>No encontramos resultados</strong>
            <span>Probá con el título, CUIJ o número de expediente.</span>
          </div>
        ) : (
          <div className="expediente-drafts-sidebar-state is-empty">
            <MessageCircle size={22} />
            <strong>Todavía no hay escritos ni conversaciones</strong>
            <span>
              {indexOnly
                ? "Los escritos y las conversaciones vinculadas aparecerán acá."
                : "Creá un escrito o vinculá una conversación desde Justiniano."}
            </span>
          </div>
        )}
      </aside>
      )}

      {!indexOnly && selectedDocumentId && (
      <div className="expediente-draft-main">
        {error && <div className="inline-error expediente-draft-error">{error}</div>}
        {documentLoading || !active ? (
          <div className="expediente-draft-empty">
            <LoaderCircle className="spin" size={26} />
            <p>Preparando el escrito…</p>
          </div>
        ) : (
          <>
            <header className="expediente-draft-header">
              <div className="expediente-draft-title-field">
                <input
                  aria-label="Título del escrito"
                  maxLength={240}
                  onChange={(event) => {
                    setTitle(event.target.value);
                    setDirty(true);
                  }}
                  value={title}
                />
                <small className={dirty ? "is-dirty" : ""}>
                  {saving ? <LoaderCircle className="spin" size={12} /> : <Save size={12} />}
                  {statusLabel}
                </small>
              </div>
              <button
                aria-label="Cerrar escrito"
                className="expediente-draft-close"
                onClick={closeDocument}
                title="Cerrar escrito y volver a Escritos y conversaciones"
                type="button"
              >
                <X size={18} />
              </button>
            </header>
            <DocumentEditor
              contextLabel="el expediente vinculado"
              onChange={(nextContent) => {
                setContent(nextContent);
                setDirty(true);
              }}
              onRequestSuggestion={(input) =>
                api.suggestWorkspaceDocument(active.id, input)}
              onRequestAiEdit={(input) =>
                api.editWorkspaceDocumentWithAi(active.id, {
                  ...input,
                })}
              onRetrySuggestionContext={() =>
                prepareWritingContext(active.id, { force: true })}
              suggestionContextError={writingContext.error || ""}
              suggestionContextStatus={writingContext.status}
              toolbarActions={(
                <>
                  <label
                    className={`workspace-document-signature-toggle ${
                      signatureConfigured ? "" : "is-unavailable"
                    }`}
                    title={signatureConfigured
                      ? "Agregar la firma configurada al final del Word"
                      : "Configurá tu firma desde Cuenta para poder incluirla"}
                  >
                    <input
                      checked={includeSignature}
                      disabled={
                        signatureLoading
                        || (!signatureConfigured && !includeSignature)
                      }
                      onChange={(event) => {
                        setIncludeSignature(event.target.checked);
                        setDirty(true);
                      }}
                      type="checkbox"
                    />
                    Incluir firma
                  </label>
                  <button
                    aria-label={active.status === "ready"
                      ? "Volver a borrador"
                      : "Marcar listo"}
                    aria-pressed={active.status === "ready"}
                    className={`tribunalea-document-toolbar-action ${
                      active.status === "ready" ? "is-active" : ""
                    }`}
                    onClick={markReady}
                    title={active.status === "ready"
                      ? "Volver a borrador"
                      : "Marcar listo"}
                    type="button"
                  >
                    <Check size={16} />
                  </button>
                  <button
                    aria-label="Descargar Word"
                    className="tribunalea-document-toolbar-action"
                    disabled={exporting}
                    onClick={exportWord}
                    title="Descargar Word"
                    type="button"
                  >
                    {exporting
                      ? <LoaderCircle className="spin" size={16} />
                      : <Download size={16} />}
                  </button>
                  <button
                    aria-label="Eliminar escrito"
                    className="tribunalea-document-toolbar-action is-danger"
                    onClick={() => setDeleteConfirmationOpen(true)}
                    title="Eliminar escrito"
                    type="button"
                  >
                    <Trash2 size={16} />
                  </button>
                </>
              )}
              documentFooter={includeSignature && signatureConfigured ? (
                <div className={`workspace-document-signature is-${
                  legalSignature.alignment || "left"
                }`}>
                  {legalSignature.imageDataUrl && (
                    <img alt="Firma configurada" src={legalSignature.imageDataUrl} />
                  )}
                  {legalSignature.displayName && <strong>{legalSignature.displayName}</strong>}
                  {legalSignature.role && <span>{legalSignature.role}</span>}
                  {legalSignature.registration && <span>{legalSignature.registration}</span>}
                </div>
              ) : null}
              value={content}
            />
            {deleteConfirmationOpen && (
              <ConfirmDialog
                busy={deleting}
                confirmLabel={deleting ? "Eliminando…" : "Eliminar escrito"}
                description="El escrito y sus cambios se eliminarán de Praxis. Esta acción no se puede deshacer."
                onClose={() => {
                  if (!deleting) setDeleteConfirmationOpen(false);
                }}
                onConfirm={deleteDocument}
                title={`¿Eliminar “${title}”?`}
              />
            )}
          </>
        )}
      </div>
      )}
      {creationDialogOpen && (
        <NewWorkspaceDocumentDialog
          busy={creating}
          error={error}
          expedientes={expedientes}
          fixedExpediente={fixedExpediente}
          onClose={() => {
            if (!creating) {
              setCreationDialogOpen(false);
              setError("");
            }
          }}
          onCreate={createBlank}
        />
      )}
    </section>
  );
}
